import { Box } from '@mui/material';
import { styled } from '@mui/system';

/**
 * Print-friendly assignment handout. Screen view is a centered "page" that
 * looks like a Letter-size sheet; print view drops the chrome and lets the
 * browser render the page natively. All measurements are tuned so the
 * handout fits a single Letter page for a typical 5-block writing plan.
 */
export default styled(Box)(() => ({
  backgroundColor: '#F2EEE5',
  minHeight: '100vh',
  padding: '7rem 1rem 2rem',
  display: 'flex',
  justifyContent: 'center',
  fontFamily: '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
  color: '#1e1e1e',

  '.handout-loading': {
    backgroundColor: '#FFFFFF',
    padding: '3rem',
    borderRadius: '8px',
    fontFamily: 'inherit',
    color: '#666',
  },

  '.handout-page': {
    backgroundColor: '#FFFFFF',
    width: '100%',
    maxWidth: '8.5in',
    minHeight: '11in',
    padding: '0.5in 0.6in',
    boxShadow: '0 4px 24px rgba(0,0,0,.08)',
    position: 'relative',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },

  '.handout-controls': {
    position: 'absolute',
    top: '-5.5rem',
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',

    '.starter-toggle': {
      flex: 1,
      minWidth: '20rem',
      backgroundColor: '#FFFFFF',
      border: '1px solid rgba(22,53,103,.15)',
      borderRadius: '.6rem',
      boxShadow: '0 2px 8px rgba(0,0,0,.05)',
      padding: '.65rem .9rem',
      display: 'flex',
      alignItems: 'center',
      gap: '.85rem',
      fontFamily: 'inherit',

      '.starter-toggle-copy': {
        flex: 1,
        fontSize: '.78rem',
        lineHeight: 1.45,
        color: '#444',

        strong: {
          color: '#163567',
          marginRight: '.15rem',
        },
      },

      '.starter-toggle-btn': {
        flex: '0 0 auto',
        backgroundColor: 'transparent',
        color: '#5C4FB8',
        border: '1.5px solid rgba(92,79,184,.5)',
        borderRadius: '999px',
        padding: '.35rem .85rem',
        fontFamily: 'inherit',
        fontWeight: 700,
        fontSize: '.78rem',
        cursor: 'pointer',
        whiteSpace: 'nowrap',

        '&:hover': {
          backgroundColor: 'rgba(92,79,184,.08)',
          borderColor: '#5C4FB8',
        },
      },
    },
  },

  '.print-button': {
    flex: '0 0 auto',
    backgroundColor: '#5C4FB8',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '999px',
    padding: '.55rem 1.1rem',
    fontFamily: 'inherit',
    fontWeight: 700,
    fontSize: '.85rem',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(92,79,184,.3)',
    whiteSpace: 'nowrap',

    '&:hover': {
      backgroundColor: '#4A3FA0',
    },
  },

  '.name-row': {
    display: 'flex',
    gap: '1.5rem',
    fontSize: '.85rem',
    color: '#555',
    marginBottom: '.9rem',

    '.name-line': {
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: '.5rem',
      flex: 1,

      '&:last-child': { flex: '0 0 auto' },
    },

    '.name-label': {
      fontWeight: 700,
      color: '#1e1e1e',
    },

    '.name-blank': {
      flex: 1,
      borderBottom: '1.5px solid #999',
      height: '1.2em',
      minWidth: '6rem',

      '&.short': { minWidth: '4rem', flex: '0 0 4rem' },
    },
  },

  '.handout-title': {
    fontFamily: '"Alfa Slab One", "Open Sans", sans-serif',
    fontSize: '1.7rem',
    fontWeight: 400,
    color: '#163567',
    margin: '0 0 .8rem 0',
    lineHeight: 1.15,
  },

  '.prompt-box': {
    backgroundColor: '#F6F6FA',
    border: '1px solid rgba(22,53,103,.15)',
    borderRadius: '.4rem',
    padding: '.7rem .9rem',
    marginBottom: '1.1rem',

    '.prompt-label': {
      fontSize: '.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.06em',
      color: '#5C4FB8',
      marginBottom: '.3rem',
    },

    '.prompt-text': {
      fontSize: '.9rem',
      lineHeight: 1.5,
      color: '#1e1e1e',
      whiteSpace: 'pre-wrap',
    },
  },

  '.blocks': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    marginBottom: '.7rem',
  },

  '.block-area': {
    flex: 1,
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
    display: 'flex',
    flexDirection: 'column',

    '.block-label': {
      fontSize: '.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.05em',
      color: '#5C4FB8',
      marginBottom: '.15rem',
    },

    // The actual writing box. Sized to fit a 5-block personal-narrative
    // handout on a single Letter page (8.5x11) with all the trimmings.
    // ~1.3in is roughly 3-4 handwritten lines for primary-grade students.
    '.block-box': {
      flex: 1,
      border: '1.5px solid #B8B8B8',
      borderRadius: '.3rem',
      minHeight: '1.3in',
      padding: '.4rem .55rem',
      backgroundColor: '#FFFFFF',
      position: 'relative',
    },

    '&.block-title': {
      flex: '0 0 auto',
    },
    '&.block-title .block-box': {
      minHeight: '.5in',
      flex: '0 0 auto',
    },

    '.block-placeholder': {
      color: '#D8D8D8',
      fontSize: '.85rem',
      fontStyle: 'italic',
      lineHeight: 1.4,
    },
  },

  '.word-supports': {
    display: 'flex',
    flexDirection: 'column',
    gap: '.35rem',
    borderTop: '2px dashed rgba(22,53,103,.15)',
    paddingTop: '.7rem',
    marginTop: '.4rem',
  },

  '.word-row': {
    display: 'flex',
    alignItems: 'baseline',
    gap: '.5rem',
    flexWrap: 'wrap',

    '.word-label': {
      fontSize: '.72rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.05em',
      flex: '0 0 auto',
      minWidth: '7.5rem',
    },

    '.word-pills': {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '.3rem',
      flex: 1,
    },

    '.word-pill': {
      backgroundColor: '#F6F6FA',
      border: '1px solid rgba(0,0,0,.1)',
      borderRadius: '999px',
      padding: '.1rem .55rem',
      fontSize: '.78rem',
      color: '#1e1e1e',
    },

    '&.heart .word-label': { color: '#D14F73' },
    '&.heart .word-pill': {
      backgroundColor: 'rgba(209,79,115,.06)',
      borderColor: 'rgba(209,79,115,.25)',
      color: '#A03D5B',
    },

    '&.important .word-label': { color: '#E88F25' },
    '&.important .word-pill': {
      backgroundColor: 'rgba(232,143,37,.07)',
      borderColor: 'rgba(232,143,37,.3)',
      color: '#B5651D',
    },

    '&.signal .word-label': { color: '#2E2C75' },
    '&.signal .word-pill': {
      backgroundColor: 'rgba(46,44,117,.06)',
      borderColor: 'rgba(46,44,117,.25)',
      color: '#2E2C75',
    },
  },

  '.handout-footer': {
    marginTop: '.9rem',
    paddingTop: '.5rem',
    borderTop: '1px solid rgba(0,0,0,.08)',
    fontSize: '.65rem',
    color: '#999',
    textAlign: 'right',
  },

  // ── Print styles ────────────────────────────────────────────────────────
  // Strip the screen chrome, let the browser render the page natively, and
  // keep block boxes from breaking across pages. Letter margins handled in
  // @page so the host page padding doesn't double up.
  '@media print': {
    backgroundColor: '#FFFFFF',
    padding: 0,
    minHeight: 'auto',

    '.no-print': { display: 'none !important' },

    '.handout-page': {
      boxShadow: 'none',
      padding: 0,
      maxWidth: '100%',
    },

    '.block-box': {
      backgroundColor: 'transparent !important',
    },
  },

  '@page': {
    size: 'Letter',
    margin: '0.5in',
  },
}));
